import { PROFESSIONAL_TRIAL_LIMIT, PREMIUM_TRIAL_LIMIT } from "@shared/tiers";
import { WORKFLOW_NOT_INCLUDED_NOTE } from "./plans";
import { CHECKOUT_FAQ_QUESTION, CHECKOUT_FAQ_ANSWER } from "./apiPlanCopy";

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqCategory {
  category: string;
  items: FaqItem[];
}

export const faqData: FaqCategory[] = [
  {
    category: "Privacy & how your files are handled",
    items: [
      {
        question: "Are my files uploaded to your servers?",
        answer:
          "No, for the large majority of tools. Most tools process everything directly in your browser using your device's own processing power \u2014 your file never leaves your computer. A small number of tools that genuinely require server-side processing say so clearly on that tool's page.",
      },
      {
        question: "How can I verify that a tool isn't uploading my file?",
        answer:
          "Open your browser's developer tools (F12), go to the Network tab, and use the tool as normal. For any client-side tool, you won't see a request carrying your file's contents leave your device.",
      },
      {
        question: "Do you store or log the contents of my files?",
        answer:
          "No. For client-side tools, we have no way to see your file at all \u2014 it never reaches us. We don't want that liability, and it's not necessary for the tool to work.",
      },
      {
        question: "Is this safe to use on a work computer with strict IT policies?",
        answer:
          "Since most tools process files locally and don't require an account, they typically fall outside the kind of network-level restrictions that block AI chat tools. Worth checking with your IT team if your organization has specific software policies.",
      },
    ],
  },
  {
    category: "Trial uses & pricing",
    items: [
      {
        question: "Can I try FreeDailyPro before paying?",
        answer:
          `Yes. Professional Tools give you ${PROFESSIONAL_TRIAL_LIMIT} trial uses a day and Premium Applications give you ${PREMIUM_TRIAL_LIMIT}, with no forced signup. When you need more, a $1 Day Pass or a $4.99 monthly plan (Premium Applications: a $3 Day Pass or $9.99 a month) gives unlimited use. The trial is the full tool, not a crippled version.`,
      },
      {
        question: "Are there daily limits on the tools?",
        answer:
          `Professional Tools share ${PROFESSIONAL_TRIAL_LIMIT} trial uses per day. Premium Applications have their own ${PREMIUM_TRIAL_LIMIT} trial uses per day, and the page shows how many you have left. A Day Pass or Monthly plan removes the cap.`,
      },
      {
        question: "What is the $1 Day Pass?",
        answer:
          `An optional pass: sign in, pay $1, and use every file-based Professional Tool without the daily trial cap for 24 hours. It is a one-time payment, not a subscription, and it does not renew. ${WORKFLOW_NOT_INCLUDED_NOTE}`,
      },
      {
        question: CHECKOUT_FAQ_QUESTION,
        answer: CHECKOUT_FAQ_ANSWER,
      },
      {
        question: "Do you have a subscription plan?",
        answer:
          `Yes. Monthly Pro is $4.99 a month: unlimited use of file-based Professional Tools and an ad-free experience, renewing until you cancel. You can cancel at any time from your Profile page (billing is handled by Stripe), and access continues to the end of the paid period. Tools that need no file are not limited for anyone. ${WORKFLOW_NOT_INCLUDED_NOTE}`,
      },
    ],
  },
  {
    category: "Account & access",
    items: [
      {
        question: "Do I need to create an account?",
        answer:
          "Not to try the tools: most work immediately with no signup. You do not need to sign in to buy: a Day Pass takes just your email, and a monthly plan takes it on the secure payment page, after which we email you a link to sign in and your access is tied to that email. An account is also useful",
      },
      {
        question: "Can I use FreeDailyPro on my phone?",
        answer:
          "Yes, the site works in any mobile browser, and you can add it to your home screen for quick access without needing to install anything from an app store.",
      },
      {
        question: "Is there a desktop or mobile app?",
        answer:
          "Not a traditional app-store app \u2014 instead, you can add a one-click shortcut to your desktop or phone's home screen directly from the browser, which opens the site instantly.",
      },
    ],
  },
  {
    category: "Technical & compatibility",
    items: [
      {
        question: "Which browsers are supported?",
        answer:
          "Any recent version of Chrome, Edge, Firefox, or Safari. A few advanced tools (like background removal) work best in Chromium-based browsers due to underlying web technology support.",
      },
      {
        question: "Is there a file size limit?",
        answer:
          "Client-side tools are limited by your own device's memory rather than an artificial cap we impose \u2014 very large files may be slower on older devices, but there's no arbitrary size restriction from our side.",
      },
      {
        question: "Why is a specific tool slow the first time I use it?",
        answer:
          "Some tools (particularly audio, video, and OCR tools) download a small processing engine the first time you use any tool in that category. After that first load, it's cached and every related tool loads instantly.",
      },
      {
        question: "I found a bug \u2014 how do I report it?",
        answer:
          "Use the feedback button on the specific tool's page \u2014 it's the fastest way, since it tells us exactly where you were. You can also reach us through the Contact page.",
      },
      {
        question: "What file types can I combine in Book Writing Toolbox?",
        answer:
          "Book Writing Toolbox is a chapter editor: you write or paste your chapters directly (headings, bold/italic/underline, lists, tables, scene breaks and images with alt text), and pick a publishing platform such as Amazon KDP, IngramSpark, Barnes & Noble Press, Lulu, Draft2Digital, Apple Books or Kobo. If you instead want to combine finished PDF, Word and image files into one document, use Document Assembler.",
      },
      {
        question: "Why does my book's page numbering look different in the front matter vs. the chapters?",
        answer:
          "That's intentional, not a bug \u2014 it follows real publishing convention. Front matter (title page, dedication, preface, table of contents) is numbered with roman numerals (i, ii, iii...), while your actual chapters restart at Arabic page 1. Preview and the Pre-flight Check show exactly how your pages will be numbered before you download.",
      },
      {
        question: "Do Book Writing Toolbox and Magazine Maker work the same way?",
        answer:
          "No. Magazine Maker builds a multi-article issue (type or paste articles, or combine finished PDF, Word and image files) with a cover and contents page, while Book Writing Toolbox is a full chapter editor with a platform picker, Preview and Pre-flight, and EPUB, Shunn-format manuscript and ONIX exports. Both use consistent page numbering and an automatic Table of Contents. Book Writing Toolbox adds a title page, dedication, preface and about-the-author page.",
      },
      {
        question: "Can I add a real cover image to my book, not just a text title page?",
        answer:
          "Yes \u2014 upload a front and/or back cover image in Book Writing Toolbox, and your title and author name are laid over it with a legibility band, the way most real book covers are designed. Leave the image out and it falls back to a clean, centered text-only title page instead \u2014 both are fully optional.",
      },
      {
        question: "Why don't the cover, Table of Contents, and copyright pages show a page number?",
        answer:
          "That's real publishing convention, not a bug. In professionally published books, the cover, Table of Contents, and copyright/imprint page are counted in the pagination but never printed with a visible folio. Roman numerals start becoming visible from the Dedication/Preface onward, and Arabic numbers begin at your first chapter.",
      },
      {
        question: "What's the difference between the QR code and barcode options on my back cover?",
        answer:
          "A QR code encodes a link \u2014 your website, a store page, anything a phone camera can scan and open. A real barcode (Code 128, EAN-13, UPC, etc.) encodes a specific number in the exact format retail and inventory scanners expect, and is generated using the same real barcode engine as our standalone Barcode Generator tool \u2014 not a decorative placeholder.",
      },
      {
        question: "What book size should I pick?",
        answer:
          "6x9 inches is the standard trade paperback size most published books use \u2014 a solid default. 5x8 and 5.5x8.5 are smaller, common for mass-market or digest-size books. US Letter and A4 are meant for document-style content (a bound collection of reports or resumes, for example) rather than a book meant to feel like a book. Whatever you pick, every page \u2014 regardless of its original size \u2014 is uniformly fit to that size, so a mix of different-sized source files still comes out as one consistent book.",
      },
      {
        question: "What is the spine option, and why does it need a width?",
        answer:
          "It's an optional printable page \u2014 title text (running vertically), an optional logo, and an optional QR code or barcode \u2014 sized to whatever width you enter, for reference or DIY binding use. It's intentionally not a print-ready wrap cover (front + spine + back as one continuous image sized to your exact page count and paper stock) \u2014 getting that right needs assumptions about your specific printer that this tool has no way to know, so it isn't faked here.",
      },
      {
        question: "What's the difference between Book Writing Toolbox, Magazine Maker, Catalog Maker, and Document Assembler?",
        answer:
          "Document Assembler and the Combine files modes of Magazine Maker and Catalog Maker merge finished files (PDF, Word, images) into one document with a Table of Contents and uniform page sizes; Magazine Maker also lets you write articles directly and adds a cover, masthead and contributors page (Combine files); Catalog Maker adds a cover, product index and price list; Document Assembler is the plain version with a cover, Table of Contents and your content. Book Writing Toolbox is different: a chapter editor with a publishing-platform picker, Preview and Pre-flight, and EPUB, manuscript and ONIX exports.",
      },
      {
        question: "Do Excel to PDF and PowerPoint to PDF preserve the original formatting exactly?",
        answer:
          "They preserve the real content \u2014 actual cell values in a genuine table grid for Excel, actual slide text and titles for PowerPoint \u2014 rather than a rough guess. What they don't reproduce is visual design: cell colors, custom fonts, embedded charts and images, or exact slide layout positioning. If the visual design is the point of the file (a branded slide deck, a color-coded spreadsheet), exporting directly from the original Office application will preserve more of that design. If you need the data or the words, these tools get you there faster.",
      },
      {
        question: "Does Redact PDF actually remove the content, or just cover it up?",
        answer:
          "It genuinely removes it. The text, picture pixels, drawings and form fields under each box are taken out of the file itself and a black bar is drawn in their place \u2014 there's nothing left underneath for anyone to extract, unlike a simple black box drawn on top of live text (which is still fully readable if you select and copy it). Everything else stays as it was: the rest of the page is still real, selectable, searchable text in its own fonts. Only in rare cases (for example a curved drawing partly under a box) can a page not be edited exactly; that page alone is then turned into a picture with the boxes painted black, and the tool tells you which page and why. Pages you don't mark are not touched.",
      },
      {
        question: "Can Repair PDF fix any broken PDF?",
        answer:
          "It fixes structural corruption \u2014 broken cross-reference tables, malformed objects, the kind of damage caused by an interrupted save or a bad file transfer \u2014 by parsing in a lenient recovery mode and rebuilding the file cleanly. It can't recover content that's genuinely missing, like a file that was truncated mid-download \u2014 there's no way to reconstruct data that was never actually written to the file in the first place.",
      },
      {
        question: "Why doesn't OCR PDF change how my document looks?",
        answer:
          "That's the point of it. The visible page stays exactly as it was scanned \u2014 what changes is invisible: real, correctly positioned text is added underneath the image, so you can now select, search, and copy text on a page that couldn't do any of that before. Pages that already had real text are left completely untouched.",
      },
      {
        question: "Can HTML to PDF convert any webpage if I give it the URL?",
        answer:
          "No \u2014 it converts HTML content you provide directly (pasted code or an uploaded .html file), not an arbitrary live website by URL. Most sites block that kind of cross-origin access from a browser for security reasons, which affects any browser-based tool, not just this one. If you want a specific webpage as a PDF, your browser's own Print dialog usually has a \"Save as PDF\" option that works directly on what you're already viewing.",
      },
      {
        question: "Why doesn't Fillable Forms show anything for my PDF?",
        answer:
          "It only works with PDFs that already have real, structured form fields built in \u2014 the kind you'd see as fillable boxes when you open the file in a PDF reader. A scanned form, or a PDF that's just laid out to look like a form without actual field objects, doesn't have anything for this tool to detect. Redact PDF and Organize PDF are for editing a PDF's content directly, which is the right tool for that kind of file instead.",
      },
    ],
  },
  {
    category: "Languages & Translation",
    items: [
      {
        question: "What languages does FreeDailyPro support?",
        answer:
          "English, Spanish, Portuguese, French, German, Japanese, Hindi, Chinese, Korean, and Indonesian. Switch between them using the language icon in the header, on desktop or mobile.",
      },
      {
        question: "Is the entire site translated, or just the menus?",
        answer:
          "Right now, navigation, the footer, and shared page elements like Contact are genuinely translated. Individual tool names, descriptions, and instructions are still in English regardless of which language you're browsing in \u2014 we're expanding translation coverage to tool content next, and didn't want to overstate where things stand today.",
      },
      {
        question: "Does switching languages change how the tools themselves work?",
        answer:
          "No. Every tool functions identically no matter which language you're browsing in \u2014 language only affects the surrounding site text, never a tool's actual behavior, accuracy, or output.",
      },
      {
        question: "How do I switch languages?",
        answer:
          "Click the language icon in the top navigation bar (next to the other header icons) and pick your language from the list. On mobile, it's inside the menu.",
      },
      {
        question: "Will more languages be added?",
        answer:
          "Yes \u2014 language support is actively being expanded, both in the number of languages available and in how much of each tool's content is translated, not just the site's navigation.",
      },
    ],
  },
];

/** A short, fixed preview set for the homepage -- one question from each
 * of the five most broadly relevant categories, in a sensible order.
 * Includes the "is everything translated?" question specifically because
 * it directly reinforces the homepage's own multilanguage trust badge and
 * language switcher -- someone seeing "Available in multiple languages"
 * right there in the FAQ, answered honestly, is exactly the point. */
export const faqPreview: FaqItem[] = [
  faqData[0].items[0], // Are my files uploaded to your servers?
  faqData[1].items[0], // Can I try FreeDailyPro before paying?
  faqData[2].items[0], // Do I need to create an account?
  faqData[1].items[2], // What is the $1 Day Pass?
  faqData[4].items[1], // Is the entire site translated, or just the menus?
];
